import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Label, Button } from 'react-bootstrap';
import moment from 'moment';
import classnames from 'classnames';
import { titleize } from 'underscore.string';

import { sluggify } from 'utils/localization/utils';
import CargomaticCard from 'components/v2/card';
import CommentList from 'components/modules/comments/CommentList';

import { withTranslation } from 'react-i18next';
import HeadingDivider from './HeadingDivider';
import ShipmentsActionCreators from '../../../actions/ShipmentsActionCreators';
import ExceptionsActionCreators from '../../../actions/ExceptionsActionCreators';
import AddAccessorialModal from '../shipments/accounting/AddAccessorialModal';
import ShipmentWindowChange from '../../ui/modals/ShipmentWindowChange';
import ShipmentStore from '../../../stores/ShipmentStore';
import SessionStore from '../../../stores/SessionStore';

class ShipmentDetails extends React.Component {
  static propTypes = {
    shipment: PropTypes.object.isRequired,
    exception: PropTypes.object.isRequired,
    onInitiateResolution: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      showAddAccessorialModal: false,
      showShipmentModal: false,
      isAdmin: SessionStore.isAdmin(),
    };
  }

  componentDidMount() {
    ShipmentStore.addChangeListener(this.getRefreshedShipment);
    ShipmentStore.addChangeListener(this.handleChange);
    ShipmentStore.addAccessorialChangeListener(this.handleAccessorialError);
  }

  componentWillUnmount() {
    ShipmentStore.removeChangeListener(this.handleChange);
    ShipmentStore.removeChangeListener(this.getRefreshedShipment);
    ShipmentStore.removeAccessorialChangeListener(this.handleAccessorialError);
  }

  getRefreshedShipment = () => {
    ShipmentStore.getShipment(this.props.shipment._id);
  };

  updateHold(on_hold) {
    const {
      _id: exception_id,
      shipment: { _id: shipment_id },
      stop: { _id: stop_id },
      exception_type,
    } = this.props.exception;

    const payload = {
      exception_id,
      shipment_id,
      stop_id,
      exception_type,
      updates: {
        on_hold,
      },
    };
    ExceptionsActionCreators.updateException(payload);
  }

  updateShipment(data) {
    ShipmentsActionCreators.updateShipmentStopWindow(
      this.props.shipment,
      this.props.exception.stop,
      data,
    );
  }

  handleAddAccessorial = (shipment, key, meta) => {
    ShipmentsActionCreators.addAccessorial(shipment, key, meta);
  };

  handleRemoveAccessorial = (shipment, key) => {
    ShipmentsActionCreators.removeAccessorial(shipment, key);
  };

  handleAccessorialError = () => {
    const error = ShipmentStore.getAddAccessorialError();
    this.setState({
      accessorialError: error ? { error } : null,
    });
  };

  handleChange = () => {
    const exceptionShipment = ShipmentStore.getShipment(
      this.props.shipment._id,
    );
    const exception = Object.assign({}, this.state.exception);
    exception.shipment = exceptionShipment;
    this.props.onChange(exception);
  };

  handleAddComment = async (comment) => {
    this.props.onAddComment(comment);
  };

  renderResolveButton() {
    const classNames = classnames({
      button: true,
      'button-green': true,
      'mr-2': true,
      'mt-6': true,
    });
    const { t } = this.props;

    return (
      <button
        type="button"
        onClick={() => this.props.onInitiateResolution(this.props.exception)}
        className={classNames}
      >
        {t('RESOLVE')}
      </button>
    );
  }

  renderOnHoldButtons() {
    const defaultClass = {
      button: true,
      'button-yellow': true,
      'mr-2': true,
      'mt-6': true,
    };
    const classNames = classnames(defaultClass);
    const disabledClass = classnames({
      ...defaultClass,
      ...{ 'opacity-50': true, 'cursor-not-allowed': true },
    });
    const { t } = this.props;
    return (
      <span>
        <button
          className={this.props.exception.on_hold ? disabledClass : classNames}
          type="button"
          onClick={() => this.updateHold(true)}
          disabled={this.props.exception.on_hold}
        >
          {t('PLACE_ON_HOLD')}
        </button>
        <button
          className={this.props.exception.on_hold ? classNames : disabledClass}
          type="button"
          onClick={() => this.updateHold(false)}
          disabled={!this.props.exception.on_hold}
        >
          {t('CLEAR_HOLD')}
        </button>
      </span>
    );
  }

  render() {
    const { t } = this.props;
    return (
      <div>
        <HeadingDivider text={t('JOB_STATUS')} />
        <Row style={{ paddingTop: 5 }}>
          <Col md={5}>
            <Label bsStyle="danger">
              {titleize(t(sluggify(this.props.shipment.status)))}
            </Label>
          </Col>
        </Row>
        <HeadingDivider text={t('LOCATION_DETAILS')} />
        <Row style={{ paddingTop: 5 }}>
          <Col md={3}>
            <span style={{ color: 'gray' }}>{t('ORIGIN')}</span>
          </Col>
          <Col md={9}>{this.props.shipment.origin[0].address}</Col>
        </Row>
        <Row>
          <Col md={3}>
            <span style={{ color: 'gray' }}>{t('DESTINATION')}</span>
          </Col>
          <Col md={9}>{this.props.shipment.destination[0].address}</Col>
        </Row>
        <Row style={{ paddingTop: 5 }}>
          <Col md={3}>
            <span style={{ color: 'gray' }}>{t('PICKUP')}</span>
          </Col>
          <Col md={9}>
            {moment(this.props.shipment.pickup_after).format('L LT')} -{' '}
            {moment(this.props.shipment.pickup_by).format('L LT')}
          </Col>
        </Row>
        <Row>
          <Col md={3}>
            <span style={{ color: 'gray' }}>{t('DELIVER')}</span>
          </Col>
          <Col md={9}>
            {moment(this.props.shipment.deliver_after).format('L LT')} -{' '}
            {moment(this.props.shipment.deliver_by).format('L LT')}
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <Button
              bsStyle="info"
              style={{ marginRight: 10 }}
              onClick={() => this.setState({ showAddAccessorialModal: true })}
            >
              {t('MANAGE_ACCESSORIALS')}
            </Button>
            <Button
              bsStyle="info"
              style={{ marginRight: 10 }}
              onClick={() => this.setState({ showShipmentModal: true })}
            >
              {t('MANAGE_SHIPMENT_WINDOW')}
            </Button>
            {this.renderResolveButton()}
          </Col>
          <AddAccessorialModal
            shipment={this.props.shipment}
            show={this.state.showAddAccessorialModal}
            addAccessorial={this.handleAddAccessorial}
            accessorialError={this.state.accessorialError}
            removeAccessorial={this.handleRemoveAccessorial}
            requireApproval={
              this.props.shipment.shipper.requires_accessorial_approval
            }
            onHide={() => this.setState({ showAddAccessorialModal: false })}
          />
          <ShipmentWindowChange
            stop={this.props.exception.stop}
            shipment={this.props.shipment}
            onHideModal={() => this.setState({ showShipmentModal: false })}
            show={this.state.showShipmentModal}
            onSave={(data) => this.updateShipment(data)}
          />
        </Row>
        <Row>
          <Col xs={12}>{this.renderOnHoldButtons()}</Col>
        </Row>
        <Row>
          <CargomaticCard
            className="mt-6"
            header={() => <h3 className="my-0">{t('DISCUSSION')}</h3>}
          >
            <CommentList
              comments={this.props.shipment.comments}
              onCommentAdd={this.handleAddComment}
              allowComment={this.state.isAdmin}
              paginate
            />
          </CargomaticCard>
        </Row>
      </div>
    );
  }
}
export default withTranslation()(ShipmentDetails);
